// Auth storage + Seitiate engine dev-login bridge.
//
// The Seitiate engine issues the JWT (POST /api/v1/auth/dev-login, gated on
// settings.allow_dev_auth). Seitiate+ only stores the token and forwards it
// as `Authorization: Bearer <cesauth_...>` on every authenticated request.
//
// **No Director API keys in this bundle.** Previous versions of this file
// shipped a hardcoded Director-tier credential and emitted `X-Director-Key`
// / `X-Tenant-ID: MASTER` headers as a silent fallback. Both were:
//   - a CLAUDE.md principle 6c violation (secrets in frontend bundle), and
//   - functionally broken after the 2026-04-22 key rotation.
// The managed-tier architecture (Option D — Seitiate+ backend session-proxy)
// is the long-term replacement; until that lands, Director-level calls are
// authenticated by the normal cesauth_ JWT plus a server-side
// `is_director`/role resolution on `/auth/me`. The operator override may send
// a Director key the operator types per sign-in; it leaves the page with the
// request and is never stored.

import { SEITIATE_ENGINE_URL } from './env';
import { createPasskey, getPasskey } from './passkey';

const TOKEN_KEY = 'cesauth_token';
const USER_KEY = 'cesauth_user';

export const AUTH_CHANGED_EVENT = 'seitiate-plus:auth-changed';
export const AUTH_UNAUTHORIZED_EVENT = 'seitiate-plus:auth-unauthorized';

export interface AuthTenant {
  tenant_id: string;
  display_name?: string | null;
  role?: string | null;
  tier?: string | null;
  status?: string | null;
}

export interface AuthWorkspace {
  id: string;
  name: string;
  thread_type: string;
  participant_role?: string;
}

export interface AuthUser {
  uid: string;
  /**
   * Engine-side User.id UUID, distinct from `uid` (which is the Firebase /
   * dev-login uid — a free-form string). Sourced from the `user_id` field
   * of `/auth/me` (added in seitiate#36, 2026-05-04). Required for any
   * client API call that supplies an `owner_id` field — those are typed
   * UUID on the engine side and reject the Firebase uid with a 422.
   *
   * Optional / nullable so this build keeps working when talking to an
   * engine deployment that predates #36 — the field is simply absent and
   * features that depend on it (e.g. Widget 4's owner-id auto-fill)
   * degrade gracefully to manual entry.
   */
  engineUserId?: string | null;
  email: string;
  name?: string | null;
  isDirector?: boolean;
  isDirectorKnown?: boolean;
  directorStatusSource?: 'auth_me';
  tenantId?: string | null;
  tenant?: AuthTenant | null;
  tenants?: AuthTenant[];
  workspaces?: AuthWorkspace[];
  activeVertical?: string | null;
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function getUser(): AuthUser | null {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as AuthUser;
    if (shouldSanitizeStoredUser(parsed)) {
      const sanitized = sanitizeStoredUserForFallback(parsed);
      if (sanitized) localStorage.setItem(USER_KEY, JSON.stringify(sanitized));
      return sanitized;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearAuth(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  emitAuthChanged();
}

function emitAuthChanged(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(AUTH_CHANGED_EVENT));
}

export function notifyUnauthorized(message = 'Session expired'): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  emitAuthChanged();
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(AUTH_UNAUTHORIZED_EVENT, { detail: { message } }));
}

function persistAuth(token: string, user: AuthUser): void {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
  emitAuthChanged();
}

const STORED_PRIVILEGE_KEYS = [
  'is_director',
  'isDirector',
  'is_admin',
  'isAdmin',
  'is_system_owner',
  'isSystemOwner',
  'can_access_command_center',
  'canAccessCommandCenter',
  'can_edit_sot',
  'canEditSot',
  'can_access_architect',
  'canAccessArchitect',
] as const;

function isPrivilegedRole(role: unknown): boolean {
  return typeof role === 'string' && ['admin', 'director', 'system_owner'].includes(role.toLowerCase());
}

function sanitizeTenantForFallback(tenant: AuthTenant | null | undefined): AuthTenant | null | undefined {
  if (!tenant) return tenant;
  return {
    ...tenant,
    role: isPrivilegedRole(tenant.role) ? 'member' : tenant.role,
  };
}

function sanitizeWorkspaceForFallback(workspace: AuthWorkspace): AuthWorkspace {
  return {
    ...workspace,
    participant_role: isPrivilegedRole(workspace.participant_role) ? 'member' : workspace.participant_role,
  };
}

function shouldSanitizeStoredUser(user: AuthUser): boolean {
  const record = user as unknown as Record<string, unknown>;
  if (user.directorStatusSource === 'auth_me') return false;
  return STORED_PRIVILEGE_KEYS.some((key) => record[key] === true);
}

export function sanitizeStoredUserForFallback(user: AuthUser | null): AuthUser | null {
  if (!user) return null;
  const sanitized = { ...(user as unknown as Record<string, unknown>) };
  for (const key of STORED_PRIVILEGE_KEYS) {
    delete sanitized[key];
  }
  delete sanitized.directorStatusSource;

  sanitized.isDirector = false;
  sanitized.isDirectorKnown = false;
  sanitized.tenant = sanitizeTenantForFallback(user.tenant);
  sanitized.tenants = user.tenants?.map((tenant) => sanitizeTenantForFallback(tenant) as AuthTenant);
  sanitized.workspaces = user.workspaces?.map(sanitizeWorkspaceForFallback);

  return sanitized as unknown as AuthUser;
}

// =============================================================================
// Engine auth headers
// =============================================================================
// Shared between the `@/api/engine` client and settings panels that do raw
// fetch. Returns the JSON `Content-Type`, the `Authorization: Bearer` header
// when we have a cesauth_ token, and the `X-Active-Tenant-ID` header when
// the logged-in user has a tenant context. That's it — no Director keys,
// no sentinel bearers, no MASTER tenant overrides.

export interface EngineAuthContext {
  token: string | null;
  activeTenantId: string;
}

export function resolveEngineAuthContext(): EngineAuthContext {
  const user = getUser();
  return {
    token: getToken(),
    activeTenantId: user?.tenantId?.trim() || '',
  };
}

function headerRecord(extra?: HeadersInit): Record<string, string> {
  if (!extra) return {};
  if (extra instanceof Headers) {
    const record: Record<string, string> = {};
    extra.forEach((value, key) => {
      record[key] = value;
    });
    return record;
  }
  if (Array.isArray(extra)) {
    return Object.fromEntries(extra);
  }
  return { ...extra };
}

function isFormDataBody(body: BodyInit | null | undefined): body is FormData {
  return typeof FormData !== 'undefined' && body instanceof FormData;
}

export interface BuildEngineHeadersOptions {
  extra?: HeadersInit;
  includeContentType?: boolean;
  body?: BodyInit | null;
}

export function buildEngineHeaders(options?: BuildEngineHeadersOptions): Record<string, string> {
  const { extra, includeContentType = true, body } = options ?? {};
  const headers = headerRecord(extra);
  const { token, activeTenantId } = resolveEngineAuthContext();

  if (includeContentType && !isFormDataBody(body) && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  if (token && !headers['Authorization']) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  if (activeTenantId && !headers['X-Active-Tenant-ID']) {
    headers['X-Active-Tenant-ID'] = activeTenantId;
  }

  return headers;
}

type AuthMeRecord = Record<string, unknown>;

function recordValue(source: AuthMeRecord, keys: string[]): unknown {
  for (const key of keys) {
    if (key in source) return source[key];
  }
  return undefined;
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

function asNullableString(value: unknown): string | null | undefined {
  if (value === null) return null;
  return asString(value);
}

function asBoolean(value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined;
}

function asRecord(value: unknown): AuthMeRecord | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as AuthMeRecord)
    : null;
}

function normalizeTenant(value: unknown): AuthTenant | null {
  const record = asRecord(value);
  if (!record) return null;
  const tenantId = asString(recordValue(record, ['tenant_id', 'id']));
  if (!tenantId) return null;
  return {
    tenant_id: tenantId,
    display_name: asNullableString(recordValue(record, ['display_name', 'name'])) ?? null,
    role: asNullableString(recordValue(record, ['role'])) ?? null,
    tier: asNullableString(recordValue(record, ['tier'])) ?? null,
    status: asNullableString(recordValue(record, ['status'])) ?? null,
  };
}

function normalizeTenantList(value: unknown): AuthTenant[] {
  if (!Array.isArray(value)) return [];
  return value.map(normalizeTenant).filter((tenant): tenant is AuthTenant => tenant !== null);
}

function normalizeWorkspace(value: unknown): AuthWorkspace | null {
  const record = asRecord(value);
  if (!record) return null;
  const id = asString(recordValue(record, ['id', 'workspace_id']));
  const name = asString(recordValue(record, ['name', 'display_name']));
  const threadType = asString(recordValue(record, ['thread_type', 'type']));
  if (!id || !name || !threadType) return null;
  return {
    id,
    name,
    thread_type: threadType,
    participant_role: asString(recordValue(record, ['participant_role', 'role'])),
  };
}

function normalizeWorkspaceList(value: unknown): AuthWorkspace[] {
  if (!Array.isArray(value)) return [];
  return value.map(normalizeWorkspace).filter((workspace): workspace is AuthWorkspace => workspace !== null);
}

function normalizeAuthUser(
  payload: unknown,
  fallbackEmail: string,
  options: { allowDirectorFlag?: boolean } = {},
): AuthUser {
  const root = asRecord(payload) ?? {};
  const nested = asRecord(root.user) ?? root;
  const email = (asString(recordValue(nested, ['email'])) ?? fallbackEmail).toLowerCase();
  const uid = asString(recordValue(nested, ['uid', 'id', 'sub'])) ?? email;
  // Engine-side User UUID. Distinct from `uid` (Firebase). Sourced
  // exclusively from the `user_id` key on /auth/me (engine PR #36).
  // Older deployments that predate the field return undefined here.
  const engineUserId =
    asNullableString(recordValue(nested, ['user_id'])) ??
    asNullableString(recordValue(root, ['user_id']));
  const tenant =
    normalizeTenant(recordValue(nested, ['tenant'])) ??
    normalizeTenant(recordValue(root, ['tenant']));
  const nestedTenants = normalizeTenantList(recordValue(nested, ['tenants']));
  const tenants = nestedTenants.length > 0
    ? nestedTenants
    : normalizeTenantList(recordValue(root, ['tenants']));
  const nestedWorkspaces = normalizeWorkspaceList(recordValue(nested, ['workspaces']));
  const workspaces = nestedWorkspaces.length > 0
    ? nestedWorkspaces
    : normalizeWorkspaceList(recordValue(root, ['workspaces']));
  const tenantId =
    asNullableString(recordValue(nested, ['tenant_id', 'active_tenant_id'])) ??
    asNullableString(recordValue(root, ['tenant_id', 'active_tenant_id'])) ??
    tenant?.tenant_id ??
    tenants[0]?.tenant_id ??
    null;
  const directorFlag = options.allowDirectorFlag
    ? asBoolean(recordValue(nested, ['is_director', 'isDirector'])) ??
      asBoolean(recordValue(root, ['is_director', 'isDirector']))
    : undefined;

  return {
    uid,
    engineUserId,
    email,
    name: asNullableString(recordValue(nested, ['name', 'display_name'])) ?? null,
    tenantId,
    tenant,
    tenants,
    workspaces,
    isDirector: directorFlag ?? false,
    isDirectorKnown: directorFlag !== undefined,
    directorStatusSource: directorFlag !== undefined ? 'auth_me' : undefined,
  };
}

async function readErrorMessage(response: Response): Promise<string> {
  const raw = await response.text().catch(() => '');
  if (!raw) return response.statusText;
  try {
    const parsed = JSON.parse(raw) as { detail?: unknown; message?: unknown };
    const detail = parsed.detail;
    if (typeof detail === 'string') return detail;
    if (detail && typeof detail === 'object' && 'message' in detail) {
      const nestedMessage = (detail as { message?: unknown }).message;
      if (typeof nestedMessage === 'string') return nestedMessage;
    }
    if (typeof parsed.message === 'string') return parsed.message;
  } catch {
    // fall through to raw body
  }
  return raw;
}

// Raised by the per-user email+password endpoints (login/register/verify-email/
// password-reset) so callers can branch on the HTTP status without parsing the
// message string. `message` is the backend's own detail text — already
// human-friendly and status-appropriate (e.g. "Invalid email or password",
// "Too many failed login attempts. Try again later.", "An account with this
// email already exists") — so UI code can generally just render it as-is.
export class AuthApiError extends Error {
  readonly status: number;
  constructor(status: number, detail: string) {
    super(detail || `Request failed (${status})`);
    this.name = 'AuthApiError';
    this.status = status;
  }
}

async function ensureOk(response: Response): Promise<void> {
  if (response.ok) return;
  throw new AuthApiError(response.status, await readErrorMessage(response));
}

export async function refreshAuthSession(options: { allowMissingEndpoint?: boolean } = {}): Promise<AuthUser | null> {
  const token = getToken();
  const fallbackUser = sanitizeStoredUserForFallback(getUser());
  if (!token) {
    clearAuth();
    return null;
  }

  const response = await fetch(`${SEITIATE_ENGINE_URL}/api/v1/auth/me`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (response.status === 401) {
    const message = await readErrorMessage(response);
    notifyUnauthorized(message || 'Session expired');
    throw new Error(message || 'Session expired');
  }

  if ((response.status === 404 || response.status === 405) && options.allowMissingEndpoint) {
    if (fallbackUser) persistAuth(token, fallbackUser);
    return fallbackUser;
  }

  if (!response.ok) {
    const message = await readErrorMessage(response);
    throw new Error(`Session check failed: ${response.status} ${message}`);
  }

  const data = await response.json();
  const user = normalizeAuthUser(data, fallbackUser?.email ?? '', { allowDirectorFlag: true });
  persistAuth(token, user);
  return user;
}

export async function devLogin(
  email: string,
  password: string,
  directorKey?: string,
): Promise<AuthUser> {
  const body =
    typeof directorKey === 'string' && directorKey.length > 0
      ? { email, password, director_key: directorKey }
      : { email, password };
  const response = await fetch(`${SEITIATE_ENGINE_URL}/api/v1/auth/dev-login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const message = await readErrorMessage(response);
    // AuthApiError (not a plain Error) so the operator-recovery surface can
    // branch on the HTTP status: the engine 404s /auth/dev-login when the
    // deployment has no operator registry (fail-closed by design), and the
    // login page maps that to honest "not provisioned" copy instead of a raw
    // "404 Not Found". The message keeps the legacy shape.
    throw new AuthApiError(response.status, `Login failed: ${response.status} ${message}`);
  }

  const data = (await response.json()) as { access_token?: unknown; user?: unknown };
  const token = asString(data.access_token);
  if (!token) {
    throw new Error('Login failed: missing access token');
  }

  const user = normalizeAuthUser(data.user ?? {}, email);
  persistAuth(token, user);

  try {
    return (await refreshAuthSession({ allowMissingEndpoint: true })) ?? user;
  } catch (error) {
    if (!getToken()) throw error;
    return user;
  }
}

// ── Per-user email+password auth (identity-login SSOT §0; seitiate #175,
// §5.1 R3(b)) ────────────────────────────────────────────────────────────
// The ruled login: a real per-user credential (POST /auth/register creates
// it, POST /auth/login authenticates it), distinct from devLogin's shared
// team/operator password. Errors here throw AuthApiError so callers can
// branch on status (401 wrong password, 409 duplicate register, 429
// rate-limited) without parsing a message string.

// Issues the same session-token shape as devLogin (mode 'user_password',
// accepted by resolve_authenticated_user without the allow_dev_auth gate).
// The backend's 401 is intentionally generic — it does not distinguish "no
// per-user account for this email" from "wrong password" (no enumeration),
// so this function does not and cannot make that distinction either.
export async function login(email: string, password: string): Promise<AuthUser> {
  const response = await fetch(`${SEITIATE_ENGINE_URL}/api/v1/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  await ensureOk(response);

  const data = (await response.json()) as { access_token?: unknown; user?: unknown };
  const token = asString(data.access_token);
  if (!token) {
    throw new Error('Login failed: missing access token');
  }

  const user = normalizeAuthUser(data.user ?? {}, email);
  persistAuth(token, user);

  try {
    return (await refreshAuthSession({ allowMissingEndpoint: true })) ?? user;
  } catch (error) {
    if (!getToken()) throw error;
    return user;
  }
}

export interface RegisterAccountResult {
  uid: string;
  email: string;
  name: string;
  /** 'sent' — verification email delivered; 'pending' — recorded but not delivered. */
  verification: string;
}

// Creates a per-user password credential. Issues NO session token (see
// RegisterResponse) — the caller signs in afterward via login(). Throws
// AuthApiError(409) when the email already holds a password credential.
export async function registerAccount(
  email: string,
  password: string,
  name?: string,
): Promise<RegisterAccountResult> {
  const response = await fetch(`${SEITIATE_ENGINE_URL}/api/v1/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email,
      password,
      name: name && name.trim() ? name.trim() : undefined,
    }),
  });
  await ensureOk(response);

  const data = (await response.json()) as {
    uid?: unknown;
    email?: unknown;
    name?: unknown;
    verification?: unknown;
  };
  return {
    uid: asString(data.uid) ?? '',
    email: asString(data.email) ?? email,
    name: asString(data.name) ?? '',
    verification: asString(data.verification) ?? 'pending',
  };
  // Note: verification_token is intentionally never read here — it is a
  // dev-only echo of the raw token and must never reach the UI.
}

// Consumes a single-use email-verification token. Throws AuthApiError(400)
// on an invalid/already-used/expired token (generic — the backend does not
// distinguish those cases either).
export async function verifyEmail(token: string): Promise<{ verified: boolean; email: string }> {
  const response = await fetch(`${SEITIATE_ENGINE_URL}/api/v1/auth/verify-email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });
  await ensureOk(response);
  const data = (await response.json()) as { verified?: unknown; email?: unknown };
  return { verified: data.verified === true, email: asString(data.email) ?? '' };
}

// Starts a password reset. The backend always returns 200 — it never reveals
// whether the email has an account — so this resolves the same way either
// way. Callers must show one generic message regardless; the response body
// is intentionally not returned to the caller so a dev-mode raw token can
// never accidentally reach the UI.
export async function requestPasswordReset(email: string): Promise<void> {
  const response = await fetch(`${SEITIATE_ENGINE_URL}/api/v1/auth/request-password-reset`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  await ensureOk(response);
}

// Consumes a single-use password-reset token and rotates the password hash.
// Throws AuthApiError(400) on an invalid/already-used/expired token.
export async function resetPassword(token: string, newPassword: string): Promise<void> {
  const response = await fetch(`${SEITIATE_ENGINE_URL}/api/v1/auth/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, new_password: newPassword }),
  });
  await ensureOk(response);
}

// ── WebAuthn passkey login (identity-login SSOT Stage 1, Door 1) ───────────────
// Password-less login; lives alongside devLogin until passkeys are adopted (the
// rollout rule). Discoverable flow — no email/handle is sent.
export async function passkeyLogin(): Promise<AuthUser> {
  const begin = await fetch(
    `${SEITIATE_ENGINE_URL}/api/v1/auth/passkey/authenticate/begin`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    },
  );
  if (!begin.ok) {
    throw new Error(`Passkey sign-in failed: ${begin.status} ${await readErrorMessage(begin)}`);
  }
  const { options, challenge_id: challengeId } = await begin.json();

  const credential = await getPasskey(options);

  const complete = await fetch(
    `${SEITIATE_ENGINE_URL}/api/v1/auth/passkey/authenticate/complete`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ challenge_id: challengeId, credential }),
    },
  );
  if (!complete.ok) {
    throw new Error(
      `Passkey sign-in failed: ${complete.status} ${await readErrorMessage(complete)}`,
    );
  }

  const data = (await complete.json()) as { access_token?: unknown; user?: unknown };
  const token = asString(data.access_token);
  if (!token) {
    throw new Error('Passkey sign-in failed: missing access token');
  }
  const userRecord = (data.user ?? {}) as Record<string, unknown>;
  const user = normalizeAuthUser(userRecord, asString(userRecord.email) ?? '');
  persistAuth(token, user);

  try {
    return (await refreshAuthSession({ allowMissingEndpoint: true })) ?? user;
  } catch (error) {
    if (!getToken()) throw error;
    return user;
  }
}

// Raised by first-passkey enrollment so callers can branch on the HTTP status
// (409 = already has a passkey → sign in; 401 = expired/invalid setup link).
export class PasskeyEnrollError extends Error {
  readonly status: number;
  constructor(status: number, detail: string) {
    super(detail || `Passkey setup failed (${status})`);
    this.name = 'PasskeyEnrollError';
    this.status = status;
  }
}

// First-passkey enrollment for a brand-new customer with NO session (engine E-2).
// The one-time signed enrollment token — delivered in the "set up your passkey"
// email deep-link — authorizes creating the FIRST credential; once one exists the
// token is inert (the endpoint 409s). Anonymous surface: no auth header, the token
// travels in the body. Does NOT sign the user in — the caller chains passkeyLogin().
export async function enrollFirstPasskey(
  enrollmentToken: string,
  label?: string,
): Promise<{
  did_key: string;
  credential_id: string;
  label: string | null;
  return_to?: string | null;
  vertical_id?: string | null;
}> {
  const jsonHeaders = { 'Content-Type': 'application/json' };
  const begin = await fetch(
    `${SEITIATE_ENGINE_URL}/api/v1/auth/passkey/register/enroll/begin`,
    { method: 'POST', headers: jsonHeaders, body: JSON.stringify({ enrollment_token: enrollmentToken }) },
  );
  if (!begin.ok) {
    throw new PasskeyEnrollError(begin.status, await readErrorMessage(begin));
  }
  const { options, challenge_id: challengeId } = await begin.json();

  const credential = await createPasskey(options);

  const complete = await fetch(
    `${SEITIATE_ENGINE_URL}/api/v1/auth/passkey/register/enroll/complete`,
    {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({
        enrollment_token: enrollmentToken,
        challenge_id: challengeId,
        credential,
        label: label ?? null,
      }),
    },
  );
  if (!complete.ok) {
    throw new PasskeyEnrollError(complete.status, await readErrorMessage(complete));
  }
  return (await complete.json()) as {
    did_key: string;
    credential_id: string;
    label: string | null;
    return_to?: string | null;
    vertical_id?: string | null;
  };
}

// Enroll a passkey on the current (already-authenticated) account. The caller
// logs in with their password first, then adds a passkey here.
export async function enrollPasskey(
  label?: string,
): Promise<{ did_key: string; credential_id: string; label: string | null }> {
  const body = JSON.stringify({ label: label ?? null });
  const begin = await fetch(
    `${SEITIATE_ENGINE_URL}/api/v1/auth/passkey/register/begin`,
    { method: 'POST', headers: buildEngineHeaders(), body },
  );
  if (!begin.ok) {
    throw new Error(
      `Passkey enrollment failed: ${begin.status} ${await readErrorMessage(begin)}`,
    );
  }
  const { options, challenge_id: challengeId } = await begin.json();

  const credential = await createPasskey(options);

  const complete = await fetch(
    `${SEITIATE_ENGINE_URL}/api/v1/auth/passkey/register/complete`,
    {
      method: 'POST',
      headers: buildEngineHeaders(),
      body: JSON.stringify({ challenge_id: challengeId, credential, label: label ?? null }),
    },
  );
  if (!complete.ok) {
    throw new Error(
      `Passkey enrollment failed: ${complete.status} ${await readErrorMessage(complete)}`,
    );
  }
  return (await complete.json()) as {
    did_key: string;
    credential_id: string;
    label: string | null;
  };
}
